/**
 * Date formatting for CRM/HSP tables — and the one distinction that actually
 * matters here.
 *
 * Postgres DATE and TIMESTAMP both arrive as ISO strings over JSON, so they look
 * identical in a payload and are trivially formatted the wrong way. They are not
 * interchangeable:
 *
 *  - A **calendar date** (`@db.Date`: a contract's term end, a rate's effective
 *    window, a hotel's contract expiry) has no time and no timezone. Prisma
 *    serialises it as UTC midnight — "2026-12-31T00:00:00.000Z" — so rendering
 *    it in local time shows Dec 30 for every user west of UTC, i.e. the entire
 *    company. Format these in UTC.
 *  - An **instant** (a plain `DateTime`: sent_at, accepted_at, first/last
 *    booking) is a real moment that belongs in the reader's own timezone.
 *    Forcing UTC would shift "sent 6pm Denver" onto the following day.
 *
 * Extracted 2026-08-09 so the Hotels and Customers panels could not quietly get
 * this backwards; the Agreements panel had already been bitten by it.
 */

const OPTS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

/**
 * Every formatter here returns `null` — never the string `'Invalid Date'` — for a
 * value JS cannot parse, so the CALLER decides the fallback (`Field` renders an
 * em-dash, `orDash` substitutes one).
 *
 * This guard is not hypothetical: the per-page formatter this module replaced
 * carried `isNaN(d.getTime()) ? '—' : …`, and consolidating without it would have
 * regressed a malformed API value from a quiet dash into the literal text
 * "Invalid Date" on the page. Caught in review of PR #1739.
 */
function format(iso: string, opts: Intl.DateTimeFormatOptions): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', opts);
}

/**
 * Format a calendar date (`@db.Date`) — pinned to UTC so the date the team
 * negotiated is the date every timezone reads back.
 */
export function fmtCalendarDate(iso: string): string | null {
  return format(iso, { ...OPTS, timeZone: 'UTC' });
}

/** Nullable form of {@link fmtCalendarDate}. */
export function fmtMaybeCalendarDate(iso: string | null): string | null {
  return iso ? fmtCalendarDate(iso) : null;
}

/** Format a true timestamp in the reader's own timezone. */
export function fmtInstant(iso: string): string | null {
  return format(iso, OPTS);
}

/** Nullable form of {@link fmtInstant}. */
export function fmtMaybeInstant(iso: string | null): string | null {
  return iso ? fmtInstant(iso) : null;
}
